import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Add keyframe animation for fade-in effect
const fadeInAnimation = `
@keyframes solutionFadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

// Add the animation style to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = fadeInAnimation;
  document.head.appendChild(style);
}

// Application-level PDF blob cache - persists across rerenders
if (!window.pdfBlobCache) {
  // console.log('Creating global PDF cache object');
  window.pdfBlobCache = {};
  window.pdfUrls = new Set(); // Track URLs that have been cached
}

// Debug helper to show all cached PDFs
// const logCacheStatus = (url) => {
//     console.log('Current cache contains:', Object.keys(window.pdfBlobCache).length, 'PDFs');
//     console.log('Checking cache for:', url);
//     console.log('Cache keys:', Object.keys(window.pdfBlobCache));
//     console.log('URL in cache:', window.pdfUrls.has(url));
// };

export const PDFView = ({ url, initialPage = 1, onPageChange, onScaleChange, scale }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [pdfData, setPdfData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const initialRenderRef = useRef(true);
  const pageRefs = useRef({});

  // Store the last URL to prevent unnecessary reloads
  const lastUrlRef = useRef(null);

  // Debug flag to see exactly what's happening
  const [debugInfo, setDebugInfo] = useState({});
  console.log('initialPage', initialPage);

  // Create a normalized URL key for cache lookup
  const getNormalizedUrl = inputUrl => {
    try {
      // Specifically remove only the t=... parameter, preserving others
      let normalized = inputUrl.replace(/&t=\d+/, ''); // Remove &t=...
      normalized = normalized.replace(/\?t=\d+$/, ''); // Remove ?t=... if it's the *only* param at the end
      normalized = normalized.replace(/\?t=\d+&/, '?'); // Remove ?t=...& and leave '?'

      return normalized;
    } catch (e) {
      console.error('Error normalizing URL:', e);
      return inputUrl;
    }
  };

  // Load PDF data - either from cache or from server
  useEffect(() => {
    // Skip reload if the URL is the same (after normalization)
    const normalizedUrl = getNormalizedUrl(url);
    // console.log(`🔍 Normalized URL for cache key: ${normalizedUrl}`);

    if (lastUrlRef.current === normalizedUrl && pdfData) {
      // console.log('🔁 Skipping reload - same PDF:', normalizedUrl);
      return;
    }

    // Update the last URL ref
    lastUrlRef.current = normalizedUrl;

    const loadPdf = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        // Log what's in the cache for debugging
        // logCacheStatus(normalizedUrl);

        // Check for cache hit in our application cache
        if (window.pdfBlobCache[normalizedUrl]) {
          // console.log('💰 APP CACHE HIT! Using cached PDF blob:', normalizedUrl);
          setPdfData(window.pdfBlobCache[normalizedUrl]);
          setDebugInfo({ source: 'cache', url: normalizedUrl });
          setIsLoading(false);
          return;
        }

        // Check if this URL is in our tracked URLs
        if (window.pdfUrls.has(normalizedUrl)) {
          // console.log('⚠️ URL in tracking set but blob not found:', normalizedUrl);
        }

        // Otherwise fetch it
        // console.log('🔄 APP CACHE MISS! Attempting fetch for:', url);

        // Fetch using the original URL (potentially with other params, handled by normalization)
        const fetchUrl = url;

        // Let the browser and server handle caching based on headers
        const response = await fetch(fetchUrl, {
          method: 'GET'
          // We rely on the server sending correct cache headers now
          // 'cache: 'force-cache'' might interfere, let's use default
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        // Store in application-level cache with the normalized URL
        // console.log('📦 Storing PDF in app cache with key:', normalizedUrl);
        window.pdfBlobCache[normalizedUrl] = blob;
        window.pdfUrls.add(normalizedUrl); // Add to our tracking set
        setPdfData(blob);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setIsError(true);
        setDebugInfo({ error: err.message });
      } finally {
        setIsLoading(false);
      }
    };

    loadPdf();
  }, [url]);

  // Effect to update page number when initialPage prop changes
  useEffect(() => {
    // Only update if the initialPage is valid and different from current pageNumber
    console.log('initialPage', initialPage);
    console.log('pageNumber', pageNumber);
    if (initialPage && initialPage !== pageNumber) {
      console.log('Updating page number to initialPage:', initialPage);
      setPageNumber(initialPage);
    }
  }, [initialPage]);

  // Effect to scroll to the current page - ONLY FOR INITIAL PAGE LOAD
  useEffect(() => {
    // Only handle the initial render case - no other automatic scrolling
    if (numPages && pageNumber && pageRefs.current[pageNumber] && initialRenderRef.current) {
      // Initial page load - scroll instantly without animation
      console.log('Initial render, scrolling to page:', pageNumber);
      pageRefs.current[pageNumber].scrollIntoView({
        behavior: 'instant',
        block: 'start'
      });
      initialRenderRef.current = false;
    }
    // No other automatic repositioning for ANY case
  }, [pageNumber, numPages, pdfData]);

  const options = useMemo(
    () => ({
      cMapUrl: 'https://unpkg.com/pdfjs-dist@4.4.168/cmaps/',
      cMapPacked: true
    }),
    []
  );

  const handlePageChange = useCallback(
    newPageNumber => {
      if (newPageNumber >= 1 && newPageNumber <= numPages) {
        setPageNumber(newPageNumber); // Update local state
        if (onPageChange) {
          onPageChange(newPageNumber);
        }
        // REMOVE ALL AUTOMATIC SCROLLING HERE - let the user control positioning
      } else {
        console.warn(`Attempted to navigate to invalid page: ${newPageNumber}`);
      }
    },
    [numPages, onPageChange]
  );

  const onZoomIn = useCallback(() => {
    const newScale = Math.min(scale + 0.2, 3.0);
    if (onScaleChange) {
      onScaleChange(newScale);
    }
  }, [scale, onScaleChange]);

  const onZoomOut = useCallback(() => {
    const newScale = Math.max(scale - 0.2, 0.5);
    if (onScaleChange) {
      onScaleChange(newScale);
    }
  }, [scale, onScaleChange]);

  const onFitToWidth = useCallback(() => {
    if (containerRef.current) {
      // Calculate scale based on container width and a standard page width (e.g., 595 points for A4 width)
      const containerWidth = containerRef.current.offsetWidth;
      const estimatedPageWidth = 595; // Assume A4 width
      const newScale = containerWidth / estimatedPageWidth;
      if (onScaleChange) {
        onScaleChange(newScale);
      }
    }
  }, [onScaleChange]);

  const onFitToPage = useCallback(() => {
    if (onScaleChange) {
      onScaleChange(1.0);
    }
  }, [onScaleChange]);

  const handleScroll = e => {
    if (containerRef.current) {
      const container = containerRef.current;
      const pages = container.querySelectorAll('.react-pdf__Page');

      let currentPage = 1;
      let maxVisibility = 0;

      pages.forEach((page, index) => {
        const rect = page.getBoundingClientRect();

        // Calculate how much of the page is visible in the viewport
        const windowHeight = window.innerHeight;
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(windowHeight, rect.bottom);

        // Skip if page is not visible at all
        if (visibleBottom <= visibleTop) {
          return;
        }

        const visibleHeight = visibleBottom - visibleTop;
        const percentVisible = visibleHeight / rect.height;

        // Track the page with the highest visibility percentage
        if (percentVisible > maxVisibility) {
          maxVisibility = percentVisible;
          currentPage = index + 1;
        }
      });

      // Only update the page number in state, but DON'T scroll to it
      // This makes the display reflect what page is visible without causing jumps
      if (currentPage !== pageNumber) {
        setPageNumber(currentPage);
        // Report page change to parent components, but don't reposition the view
        onPageChange?.(currentPage);
      }
    }
  };

  const containerRef = useRef(null);

  return (
    <div
      className="pdf-viewer h-full w-full flex flex-col"
      style={{
        position: 'absolute',
        top: '5px',
        left: '5px',
        right: '5px',
        bottom: '5px',
        height: 'calc(100% - 22px)',
        width: 'calc(100% - 10px)'
      }}
    >
      <div
        className="pdf-container relative flex-1 overflow-x-hidden h-full"
        style={{ height: '100%', width: '100%', background: 'transparent' }}
      >
        {/* Zoom controls overlay */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
          <button
            className="bg-gray-700/50 hover:bg-gray-600/50 text-white rounded-full p-2 transition-all duration-200"
            onClick={onZoomOut}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>

          <span className="bg-gray-700/50 text-white px-3 py-1 rounded-full min-w-[80px] text-center">
            {scale ? Math.round(scale * 100) : 100}%
          </span>

          <button
            className="bg-gray-700/50 hover:bg-gray-600/50 text-white rounded-full p-2 transition-all duration-200"
            onClick={onZoomIn}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>

        {/* PDF content */}
        <div
          ref={containerRef}
          className="absolute inset-0 overflow-y-auto"
          onScroll={handleScroll}
        >
          <div
            className={`flex flex-col ${scale === 1 ? 'items-center' : 'items-start'} min-h-full w-full`}
          >
            {isLoading ? (
              <div
                className="flex flex-col items-center justify-center w-full py-10"
                style={{ animation: 'solutionFadeIn 0.5s ease-out forwards' }}
              >
                <div className="animate-spin h-10 w-10 mb-3 rounded-full border-2 border-transparent border-t-[#e4b17a] border-b-[#c99b69]"></div>
                <p className="text-[#8c6b4a] text-sm">Loading document...</p>
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center w-full py-10">
                <div className="bg-red-50 p-4 rounded-lg mb-3 border border-red-200">
                  <p className="text-red-600 text-sm">
                    Failed to load PDF document. Please try again or check the file.
                  </p>
                </div>
                <button
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md mt-2 text-sm hover:bg-gray-300"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </button>
              </div>
            ) : (
              <Document
                file={pdfData}
                options={options}
                error={
                  <div className="flex items-center justify-center p-8 text-red-500 bg-red-50 rounded-lg">
                    Error loading PDF! Please try again.
                  </div>
                }
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <div key={`page_${index + 1}`} className="mb-4 w-full">
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={650 * scale}
                      scale={1}
                      className="shadow-md mx-auto relative"
                      data-page-number={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      inputRef={ref => {
                        if (ref) {
                          pageRefs.current[index + 1] = ref;

                          // If this is the initial page and we just mounted, scroll to it
                          if (index + 1 === initialPage && initialRenderRef.current && pdfData) {
                            setTimeout(() => {
                              ref.scrollIntoView({ behavior: 'auto', block: 'start' });
                            }, 100);
                          }
                        }
                      }}
                    />
                  </div>
                ))}
              </Document>
            )}
          </div>
        </div>
      </div>
      <div className="absolute inset-0 pointer-events-none">
        <div className="relative w-full h-full mx-auto">
          {/* Navigation arrows removed */}
          <button
            className="absolute right-4 bottom-8 bg-gray-700/50 hover:bg-gray-600/50 text-white rounded-full p-3 transition-all duration-200 z-50 pointer-events-auto"
            title="Download PDF"
            onClick={() => window.open(url, '_blank')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
